import { inject, Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Auth } from 'aws-amplify';
import { CognitoResponse, TotpSetupResponse, UserProfile } from '../models/interface';
import { ApiService } from './api.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ChallengeNames, StorageKeys, UserTypes } from '@/shared/helper';
import { AppPermission, RoleManager, SectionKey } from '@/core/constants/role-config';

@Injectable({ providedIn: 'root' })
export class AuthService {
    private router = inject(Router);
    private activatedRoute = inject(ActivatedRoute);
    private authState = new BehaviorSubject<any>(null);
    authState$ = this.authState.asObservable();

    private password = new BehaviorSubject<string>('');
    password$ = this.password.asObservable();

    private authTotpQR = new BehaviorSubject<TotpSetupResponse>({
        secretCode: '',
        qrCodeUrl: ''
    });
    authTotpQR$ = this.authTotpQR.asObservable();

    private showTotpSetup = new BehaviorSubject<boolean>(false);
    showTotpSetup$ = this.showTotpSetup.asObservable();

    setShowTotpSetup(value: boolean) {
        this.showTotpSetup.next(value);
    }

    private signupUser = new BehaviorSubject<CognitoResponse | null>(null);
    signupUser$ = this.signupUser.asObservable();

    private readonly STORAGE_KEY = StorageKeys.AUTH_USER;
    private readonly SESSION_KEY = StorageKeys.SESSION;

    constructor(private apiService: ApiService) {
        this.loadAuthStateFromStorage();
        if (this.activatedRoute.snapshot.queryParams['showAlert'] !== 'false') {
            this.setupForceLogoutListener();
        }
    }

    private setupForceLogoutListener(): void {
        window.addEventListener('storage', async (event) => {
            // `newValue` nulo = alguien limpió la llave, no un logout nuevo.
            if (event.key === StorageKeys.FORCE_LOGOUT && event.newValue) {
                const storedAuth = localStorage.getItem(this.STORAGE_KEY);
                if (storedAuth) {
                    const authData = JSON.parse(storedAuth);
                    const username = authData?.username;
                    if (username) {
                        try {
                            const roles = this.getStoredRoles(authData);
                            await this.clearSessionByRoles(roles);
                        } catch (error) {
                            console.error('Error fetching user group or clearing session:', error);
                        }
                    }
                }
                this.clearStorage();
                this.authState.next(null);
                this.signupUser.next(null);
                this.showTotpSetup.next(false);
                this.router.navigate(['/landing/login']);
            }
        });
    }

    private async loadAuthStateFromStorage(): Promise<void> {
        const storedAuth = localStorage.getItem(this.STORAGE_KEY);
        if (storedAuth) {
            try {
                const authData = JSON.parse(storedAuth);
                const user = await Auth.currentAuthenticatedUser().catch(() => null);
                if (user && authData) {
                    this.authState.next(authData);
                    sessionStorage.setItem(this.SESSION_KEY, 'true');
                } else {
                    const username = authData?.username;
                    if (username) {
                        const roles = this.getStoredRoles(authData);
                        await this.clearSessionByRoles(roles);
                    }
                    await this.logout();
                }
            } catch (e) {
                console.error('Error loading auth state:', e);
                await this.logout();
            }
        } else {
            this.redirectToLoginIfProtected();
        }
    }

    /**
     * Manda al login solo si la ruta actual es privada.
     *
     * Este servicio se construye en el arranque (lo inyectan los guards), así que redirigir siempre
     * sacaba al usuario de `/landing/forgot-password` o `/landing/reset-password` en cuanto entraba
     * por URL directa. Se lee de `location` y no de `router.url` porque en ese punto el router
     * todavía no corre la navegación inicial.
     */
    private redirectToLoginIfProtected(): void {
        const path = window.location.pathname;
        if (path.startsWith('/landing') || path.startsWith('/buzon')) {
            return;
        }
        this.router.navigate(['/landing/login']);
    }

    /**
     * Marca esta pestaña como dueña de la sesión (ver `TabSessionService`).
     *
     * Se llama en todo camino de login que termina con el usuario autenticado, no solo en el que
     * pasa por MFA: si la marca no queda puesta, el siguiente arranque de la pestaña lee la sesión
     * como huérfana y la cierra.
     */
    private markTabAsSessionOwner(): void {
        sessionStorage.setItem(this.SESSION_KEY, 'true');
    }

    private clearStorage(): void {
        localStorage.removeItem(this.STORAGE_KEY);
        sessionStorage.setItem(this.SESSION_KEY, 'false');
    }
    private saveAuthStateToStorage(authData: any): void {
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(authData));
        sessionStorage.setItem(this.SESSION_KEY, 'true');
        // La marca del logout anterior ya no aplica: se limpia para que no quede colgada en
        // `localStorage` y para que el siguiente `setItem` sí cuente como cambio en las otras pestañas.
        localStorage.removeItem(StorageKeys.FORCE_LOGOUT);
    }

    private normalizeRoles(roles: unknown): string[] {
        if (!Array.isArray(roles)) {
            return [];
        }
        return roles
            .filter((role): role is string => typeof role === 'string' && !!role.trim())
            .map((role) => role.toLowerCase());
    }

    private extractUserRolesFromCognitoUser(user: any): string[] {
        const groups = user?.signInUserSession?.accessToken?.payload?.['cognito:groups'];
        return this.normalizeRoles(groups);
    }

    private getStoredRoles(authData: any): string[] {
        if (Array.isArray(authData?.userRoles)) {
            return this.normalizeRoles(authData.userRoles);
        }
        if (typeof authData?.userRole === 'string' && authData.userRole.trim()) {
            return [authData.userRole.toLowerCase()];
        }
        return [];
    }

    private async clearSessionByRoles(roles: string[]): Promise<void> {
        if (roles.includes(UserTypes.PROSPECT)) {
            await this.apiService.clearProspectSession();
            return;
        }
        await this.apiService.clearCustomerSession();
    }

    // Checks if a user is currently authenticated with Cognito.
    async isAuthenticated(): Promise<boolean> {
        try {
            const user = await Auth.currentAuthenticatedUser();
            return !!user;
        } catch (error) {
            return false;
        }
    }

    // Checks for handleError
    private handleError(error: any): string {
        console.error('AuthService Error:', error);
        // Map common AWS Amplify error codes to user-friendly messages
        const errorMessages: { [key: string]: string } = {
            default: 'Ocurrió un error. Por favor, intenta de nuevo.'
        };
        if (error.code === 'UserLambdaValidationException' && error.message.includes('SESSION_ALREADY_ACTIVE')) {
            return 'Hemos detectado actividad inusual de acceso. Tu usuario ha sido bloqueado. Contacta a soporte.';
        } else if (error.code === 'UserLambdaValidationException' && error.message.includes('TOTP_ABUSE')) {
            return 'Tu cuenta ha sido bloqueada por abuso de intentos de TOTP. Contacta a soporte.';
        }
        return errorMessages[error.code] || error.message || error || error.code || errorMessages['default'];
    }

    // Registers a new user in Cognito with email, password, and account type.
    async signUp(email: string, password: string, accountType: string) {
        if (!['moral', 'fisica'].includes(accountType)) {
            throw new Error('Invalid account type. Must be "moral" or "fisica".');
        }
        try {
            const result = await Auth.signUp({
                username: email,
                password: password,
                attributes: {
                    email,
                    'custom:legalPersonality': accountType,
                    'custom:organizationId': '',
                    'custom:signUpState': 'PENDING_OCR'
                }
            });
            this.password.next(password);
            this.authState.next(result);
            return result;
        } catch (error) {
            console.error('Error signing up:', error);
            throw new Error(this.handleError(error));
        }
    }

    // Confirms a user's sign-up using the verification code sent to their email.
    async confirmSignUp(username: string, code: string) {
        try {
            const result = await Auth.confirmSignUp(username, code);
            this.authState.next(result);
            return result;
        } catch (error: any) {
            throw new Error(this.handleError(error));
        }
    }

    // Sets up Time-based One-Time Password (TOTP) for MFA, generating a secret code and QR code URL.
    async setupTotp(user: any): Promise<TotpSetupResponse> {
        try {
            const totpSetup = await Auth.setupTOTP(user);
            const secretCode = totpSetup.toString();
            const issuer = 'ARES';
            const qrCodeUrl = `otpauth://totp/${encodeURIComponent(`${secretCode}`)}?secret=${secretCode}&issuer=${encodeURIComponent(issuer)}`;
            this.showTotpSetup.next(true);
            this.authTotpQR.next({ secretCode, qrCodeUrl });
            this.authState.next({ secretCode, qrCodeUrl });
            return { secretCode, qrCodeUrl } as TotpSetupResponse;
        } catch (error: any) {
            throw new Error(this.handleError(error));
        }
    }

    // Authenticates a user with email and password, initiating the sign-in process.
    async signIn(email: string, password: string) {
        try {
            const user = await Auth.signIn(email, password);
            this.authState.next(user);
            this.signupUser.next(user);
            if (!user?.challengeName) {
                // Usuario sin MFA pendiente: la sesión ya está abierta en esta pestaña.
                this.markTabAsSessionOwner();
            }
            // Ya no validamos rol aquí - lo haremos después del MFA en confirmSignIn()
            return user;
        } catch (error: any) {
            throw new Error(this.handleError(error));
        }
    }

    // Completes sign-in by verifying the MFA OTP code.
    async confirmSignIn(otpValue: string) {
        const user = this.authState.value;
        const allowedAttempts = 3;
        if (!user) {
            throw new Error('No user found. Please sign in first.');
        }
        try {
            const result = await Auth.confirmSignIn(user, otpValue, ChallengeNames.SOFTWARE_TOKEN_MFA);
            this.authState.next(result);
            this.markTabAsSessionOwner();

            // AHORA validamos el rol - después del MFA completado
            // En este punto sí tenemos sesión válida para obtener el rol
            try {
                const userRoles = await this.getUserRoles();
                if (!RoleManager.canAccessApplication(userRoles)) {
                    const primaryRole = userRoles[0] || 'unknown';
                    console.warn(`Auth Service: Intento de acceso con roles bloqueados en confirmSignIn: '${userRoles.join(', ')}'`);

                    // Limpiar sesión inmediatamente
                    await this.logout();

                    // Lanzar error con mensaje específico
                    throw new Error(
                        primaryRole === 'customer'
                            ? 'Los clientes no tienen acceso a este módulo de administración.'
                            : primaryRole === 'prospect'
                              ? 'Los prospectos no tienen acceso a este módulo de administración.'
                              : `Tus roles '${userRoles.join(', ')}' no tienen permisos para acceder a esta aplicación.`
                    );
                }
            } catch (roleError: any) {
                // Si hay error al validar rol, logout y relanzar
                await Auth.signOut().catch(() => null);
                this.authState.next(null);
                this.signupUser.next(null);
                this.clearStorage();
                throw roleError;
            }

            // Solo si pasó la validación de rol, guardamos en storage
            this.saveAuthStateToStorage(result);
            return result;
        } catch (error: any) {
            sessionStorage.setItem(this.SESSION_KEY, 'false');
            if (error.code === 'CodeMismatchException') {
                try {
                    const signupUser = this.signupUser?.value;
                    if (!signupUser) {
                        throw new Error('No signup user data available.');
                    }
                    const username = signupUser?.username;
                    const userType = this.extractUserRolesFromCognitoUser(signupUser)[0] || 'unknown';
                    const payload = {
                        userType: userType,
                        username: username || '',
                        sessionId: ''
                    };
                    const res = await this.apiService.attemptsFailureLogin(payload);
                    if (res.attempts > allowedAttempts) {
                        throw new Error('Tu cuenta ha sido bloqueada por abuso de intentos de TOTP, contacta a soporte.');
                    } else {
                        throw new Error(`Tu código es incorrecto. Te quedan ${allowedAttempts - res.attempts} intentos`);
                    }
                } catch (error: any) {
                    throw new Error(this.handleError(error));
                }
            }
            throw error;
        }
    }
    // Completes the new password challenge when user logs in with temporary password
    async completeNewPassword(user: any, newPassword: string) {
        try {
            const result = await Auth.completeNewPassword(user, newPassword);
            this.authState.next(result);
            if (!result?.challengeName) {
                this.markTabAsSessionOwner();
            }
            return result;
        } catch (error: any) {
            throw new Error(this.handleError(error));
        }
    }
    // Sets up TOTP MFA, returns secret code for QR generation
    async setupTOTP(user: any): Promise<string> {
        try {
            const secretCode = await Auth.setupTOTP(user);
            return secretCode;
        } catch (error: any) {
            throw new Error(this.handleError(error));
        }
    }

    // Verifies TOTP token and sets it as preferred MFA method
    async verifyTotpToken(user: any, totpToken: string) {
        try {
            const result = await Auth.verifyTotpToken(user, totpToken);
            // Set TOTP as the preferred MFA method
            await Auth.setPreferredMFA(user, 'TOTP');
            return result;
        } catch (error: any) {
            throw new Error(this.handleError(error));
        }
    }

    /**
     * Descarta la sesión que Cognito abre al verificar el TOTP del reto `MFA_SETUP`.
     *
     * Ese camino nunca pasa por `confirmSignIn()`, así que no valida el rol del usuario y lo que
     * antes se guardaba en storage era la sesión de Cognito, no el usuario (sin `username` ni
     * `userRoles`). En vez de entrar con esa sesión a medias, se cierra y el usuario vuelve al login
     * a autenticarse con su primer código del autenticador.
     *
     * No reutiliza `logout()` a propósito: aquí no hay que navegar (la pantalla de login ya es la
     * actual) ni disparar `FORCE_LOGOUT`, que sacaría de sesión a las demás pestañas.
     */
    async discardTotpSetupSession(): Promise<void> {
        await Auth.signOut().catch(() => null);
        this.authState.next(null);
        this.signupUser.next(null);
        this.showTotpSetup.next(false);
        this.clearStorage();
    }
    // Initiates the password reset process by sending a verification code to the user’s email.
    async forgotPassword(email: string) {
        try {
            const result = await Auth.forgotPassword(email);
            return result;
        } catch (error: any) {
            throw new Error(this.handleError(error));
        }
    }

    // Submits a new password using the verification code from forgotPassword.
    async forgotPasswordSubmit(email: string, code: string, newPassword: string) {
        try {
            const result = await Auth.forgotPasswordSubmit(email, code, newPassword);
            return result;
        } catch (error: any) {
            throw new Error(this.handleError(error));
        }
    }

    // Retrieves the current Cognito user session, including tokens
    async getCurrentSession(): Promise<any> {
        try {
            const session = await Auth.currentSession();
            return session;
        } catch (error: any) {
            console.error('Error fetching current session:', error);
            const errorMessage = error?.message || 'Failed to fetch current session';
            throw {
                success: false,
                statusCode: error?.code || 401,
                message: errorMessage
            };
        }
    }

    // Signs out the current user, clears auth state,
    async logout() {
        try {
            await Auth.signOut();
            this.authState.next(null);
            this.signupUser.next(null);
            this.clearStorage();
            localStorage.setItem(StorageKeys.FORCE_LOGOUT, String(Date.now()));
            this.router.navigate(['/landing/login']);
        } catch (error) {
            console.error('Error signing out:', error);
            this.authState.next(null);
            this.signupUser.next(null);
            this.clearStorage();
            localStorage.setItem(StorageKeys.FORCE_LOGOUT, String(Date.now()));
            this.router.navigate(['/landing/login']);
            throw error;
        }
    }

    /**
        * Obtiene todos los roles del usuario actual.
        * Primero intenta obtenerlos del storage, si no, los toma del token de Cognito.
     */
    async getUserRoles(): Promise<string[]> {
        try {
            // Intentar obtener del storage
            const storedAuth = localStorage.getItem(this.STORAGE_KEY);
            if (storedAuth) {
                const authData = JSON.parse(storedAuth);
                const storedRoles = this.getStoredRoles(authData);
                if (storedRoles.length > 0) {
                    return storedRoles;
                }
            }

            // Si no hay storage, obtenerlo del usuario actual
            const user = await Auth.currentAuthenticatedUser().catch(() => null);
            if (user) {
                const userRoles = this.extractUserRolesFromCognitoUser(user);
                const normalizedRoles = userRoles.length > 0 ? userRoles : ['unknown'];
                // Guardar en storage
                this.saveAuthStateToStorage({
                    username: user.username,
                    userRole: normalizedRoles[0],
                    userRoles: normalizedRoles,
                    email: user.attributes?.email
                });
                return normalizedRoles;
            }

            return ['unknown'];
        } catch (error) {
            console.error('Error getting user role:', error);
            return ['unknown'];
        }
    }

    /**
     * Perfil del usuario autenticado para el topbar y la pantalla `/profile`.
     *
     * Todo sale del token de Cognito; si la sesión no se puede leer se cae a lo último que quedó
     * en `localStorage` (email/username/roles) en vez de reventar, porque el topbar lo pinta en
     * cada carga y no queremos que un token expirado tumbe el header.
     */
    async getUserProfile(): Promise<UserProfile | null> {
        const storedAuth = (() => {
            try {
                const raw = localStorage.getItem(this.STORAGE_KEY);
                return raw ? JSON.parse(raw) : null;
            } catch {
                return null;
            }
        })();

        const user = await Auth.currentAuthenticatedUser().catch(() => null);
        if (!user && !storedAuth) {
            return null;
        }

        const attributes = user?.attributes ?? {};
        const idTokenPayload = user?.signInUserSession?.idToken?.payload ?? {};
        const roles = user ? this.extractUserRolesFromCognitoUser(user) : this.getStoredRoles(storedAuth);
        const toDate = (seconds: unknown): Date | null => (typeof seconds === 'number' ? new Date(seconds * 1000) : null);

        const givenName = attributes['given_name'] || null;
        const familyName = attributes['family_name'] || null;
        const fullName = attributes['name'] || [givenName, familyName].filter(Boolean).join(' ') || null;

        return {
            userId: attributes['sub'] || idTokenPayload['sub'] || '',
            username: user?.username || storedAuth?.username || '',
            email: attributes['email'] || idTokenPayload['email'] || storedAuth?.email || '',
            emailVerified: attributes['email_verified'] === true || attributes['email_verified'] === 'true',
            fullName,
            givenName,
            familyName,
            phoneNumber: attributes['phone_number'] || null,
            roles: roles.length > 0 ? roles : ['unknown'],
            mfa: user?.preferredMFA || null,
            organizationId: attributes['custom:organizationId'] || null,
            lastSignInAt: toDate(idTokenPayload['auth_time']),
            sessionExpiresAt: toDate(idTokenPayload['exp'])
        };
    }

    async getUserRole(): Promise<string> {
        const roles = await this.getUserRoles();
        return roles[0] || 'unknown';
    }

    /**
     * Verifica si el usuario actual tiene acceso a la aplicación
     */
    async canAccessApplication(): Promise<boolean> {
        try {
            const roles = await this.getUserRoles();
            return RoleManager.canAccessApplication(roles);
        } catch (error) {
            console.error('Error checking application access:', error);
            return false;
        }
    }

    /**
     * ¿El usuario actual tiene este permiso de la matriz de roles? (ver `APP_PERMISSIONS`).
     *
     * Reemplaza a los viejos `canAccessClients()`/`canEdit()`/`canCreate()`…, que colgaban de la
     * capa de booleanos gruesos ya retirada. Nadie los llamaba, así que no hubo call sites que migrar.
     */
    async can(permission: AppPermission): Promise<boolean> {
        try {
            const roles = await this.getUserRoles();
            return RoleManager.can(roles, permission);
        } catch (error) {
            console.error(`Error checking permission ${permission}:`, error);
            return false;
        }
    }

    /** ¿Puede ver esta sección de la app? Mismo criterio que aplica el menú lateral. */
    async canAccessSection(section: SectionKey): Promise<boolean> {
        try {
            const roles = await this.getUserRoles();
            return RoleManager.canAccessSection(roles, section);
        } catch (error) {
            console.error(`Error checking section access ${section}:`, error);
            return false;
        }
    }
}
